/*
 * Account Save Modal — email capture after add-to-cart
 *
 * Anonymous-only. The prompt appears PROMPT_DELAY_MS (5s) AFTER an item is added
 * to the cart, never before it and never when the shopper reaches the editor.
 * Two trigger paths, both non-blocking:
 *
 *   1. [data-add-to-cart] click (Sample variants, regular ATC):
 *      The click is NOT intercepted; the add proceeds normally. We start a
 *      5s timer. If the page stays put (AJAX add) the modal opens there; if the
 *      add navigates to /cart the timer is lost and path 2 takes over.
 *
 *   2. /cart page auto-trigger (configurator path):
 *      The configurator's internal #add-to-cart-button is NOT intercepted.
 *      Instead, the configurator adds the item to cart and redirects to /cart
 *      as normal. 5s after /cart loads the modal auto-shows. This avoids the
 *      race condition where a "Sign in with Shop" redirect fires before the
 *      async /cart/add.js fetch can complete.
 *
 * Either way the modal only opens if /cart.js reports at least one item, so a
 * failed add or an empty cart never triggers a sign-in prompt.
 *
 * Shop login flow on /cart:
 *   Click "Sign in with Shop" -> sets asm_shop_login_pending + asm_return_to_cart
 *   in sessionStorage -> Shop login redirect happens -> after login, the Liquid
 *   {%- if customer -%} block in account-save-modal.liquid fires Klaviyo and
 *   redirects back to /cart (where the cart item is still saved).
 *
 * Show-once per session: sessionStorage["accountSaveModalSeen"] = "1".
 * Pre-fills /account/register: localStorage["pending_save_customer"].
 */
(function () {
  if (window.__accountSaveModalBound) return;
  window.__accountSaveModalBound = true;

  var SESSION_KEY = 'accountSaveModalSeen';
  var PENDING_EMAIL_KEY = 'pending_save_customer';
  var PROMPT_DELAY_MS = 5000; // wait this long after add-to-cart before asking
  var promptTimer = null;

  // Klaviyo creds — match section-klaviyo-embed.liquid
  var KLAVIYO_COMPANY_ID = 'VxShCS';
  var KLAVIYO_LIST_ID = 'RSYwiS';
  var KLAVIYO_SOURCE = 'Add to Cart - Save Selection';

  function isLoggedIn() {
    if (document.body.classList.contains('customer-logged-in')) return true;
    if (document.body.dataset && document.body.dataset.customer === 'logged-in') return true;
    if (document.documentElement.classList.contains('customer-logged-in')) return true;
    return false;
  }

  function shouldShowModal() {
    if (isLoggedIn()) return false;
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') return false;
    } catch (e) { /* sessionStorage blocked */ }
    return true;
  }

  function markSeen() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* noop */ }
  }

  function getModalEl() {
    return document.querySelector('account-save-modal');
  }

  function postKlaviyo(email) {
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: KLAVIYO_SOURCE,
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: KLAVIYO_LIST_ID } }
        }
      }
    };
    return fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(KLAVIYO_COMPANY_ID),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify(payload)
      }
    ).then(function (res) {
      if (res.status === 200 || res.status === 201 || res.status === 202) return true;
      return res.text().then(function (txt) { throw new Error(txt || ('HTTP ' + res.status)); });
    });
  }

  function showStep(modalEl, step) {
    var steps = modalEl.querySelectorAll('[data-asm-step]');
    steps.forEach(function (s) {
      s.style.display = s.getAttribute('data-asm-step') === step ? '' : 'none';
    });
  }

  function openModal() {
    var modalEl = getModalEl();
    if (!modalEl) return Promise.resolve();
    var overlay = modalEl.querySelector('#account-save-modal');
    if (!overlay) return Promise.resolve();
    overlay.style.display = 'flex';
    showStep(modalEl, 'email');
    var emailInput = modalEl.querySelector('[data-asm-email-input]');
    if (emailInput) setTimeout(function () { emailInput.focus(); }, 50);

    return new Promise(function (resolve) {
      modalEl.__asmResolve = resolve;
    });
  }

  function closeModal(modalEl) {
    var overlay = modalEl.querySelector('#account-save-modal');
    if (overlay) overlay.style.display = 'none';
    markSeen();
    if (typeof modalEl.__asmResolve === 'function') {
      var r = modalEl.__asmResolve;
      modalEl.__asmResolve = null;
      r();
    }
  }

  function attachModalHandlers() {
    var modalEl = getModalEl();
    if (!modalEl || modalEl.__asmHandlersAttached) return;
    modalEl.__asmHandlersAttached = true;

    var overlay = modalEl.querySelector('#account-save-modal');
    var contentEl = modalEl.querySelector('[data-asm-content]');
    var closeBtn = modalEl.querySelector('[data-asm-close]');
    var emailForm = modalEl.querySelector('[data-asm-form="email"]');
    var emailInput = modalEl.querySelector('[data-asm-email-input]');
    var accountEmailInput = modalEl.querySelector('[data-asm-account-email]');
    var errorEl = modalEl.querySelector('[data-asm-error]');

    // Overlay-click + close-button + skip-links all dismiss.
    overlay && overlay.addEventListener('click', function (e) {
      if (contentEl && !contentEl.contains(e.target)) closeModal(modalEl);
    });
    closeBtn && closeBtn.addEventListener('click', function () { closeModal(modalEl); });
    modalEl.querySelectorAll('[data-asm-skip]').forEach(function (btn) {
      btn.addEventListener('click', function () { closeModal(modalEl); });
    });

    // Sign in with Shop:
    //   - Immediately close our modal so the overlay doesn't linger while
    //     the Shop login popup is open.
    //   - Set asm_shop_login_pending so the post-login Liquid block can
    //     auto-subscribe to Klaviyo using {{ customer.email }}.
    //   - Set asm_return_to_cart when on /cart so the post-login block
    //     redirects the customer back to their cart after Shop auth.
    var shopLoginContainer = modalEl.querySelector('.asm-shop-login');
    shopLoginContainer && shopLoginContainer.addEventListener('click', function () {
      try {
        sessionStorage.setItem('asm_shop_login_pending', '1');
        if (window.location.pathname === '/cart') {
          sessionStorage.setItem('asm_return_to_cart', '1');
        }
      } catch (e) { /* noop */ }
      // Dismiss our overlay immediately — the Shop login popup takes over.
      closeModal(modalEl);
    });

    // Step 1 submit -> Klaviyo -> step 2.
    emailForm && emailForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (emailInput && emailInput.value || '').trim();
      if (errorEl) errorEl.textContent = '';
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (errorEl) errorEl.textContent = 'Please enter a valid email address.';
        emailInput && emailInput.focus();
        return;
      }
      emailForm.classList.add('is-loading');
      var btn = emailForm.querySelector('button[type="submit"]');
      if (btn) btn.disabled = true;

      postKlaviyo(email)
        .then(function () {
          // Store for /account/register prefill
          try { localStorage.setItem(PENDING_EMAIL_KEY, email); } catch (e) { /* noop */ }
          // Fire Klaviyo identify if SDK is present
          if (window.klaviyo && typeof window.klaviyo.push === 'function') {
            try {
              window.klaviyo.push(['identify', { '$email': email }]);
              window.klaviyo.push(['track', 'Saved Selection', { source: KLAVIYO_SOURCE }]);
            } catch (e) { /* noop */ }
          }
          // Prefill step 2 email
          if (accountEmailInput) accountEmailInput.value = email;
          showStep(modalEl, 'account');
        })
        .catch(function (err) {
          console.error('Klaviyo subscribe failed:', err);
          if (errorEl) errorEl.textContent = 'Something went wrong. Please try again, or skip to continue.';
        })
        .finally(function () {
          emailForm.classList.remove('is-loading');
          if (btn) btn.disabled = false;
        });
    });
  }

  // ---- Delayed prompt (shared by both trigger paths) ----

  function isModalOpen() {
    var modalEl = getModalEl();
    var overlay = modalEl && modalEl.querySelector('#account-save-modal');
    return !!(overlay && overlay.style.display === 'flex');
  }

  // True only when the cart really has something in it, so a failed add (or an
  // empty /cart) never gets a sign-in prompt. A fetch error counts as "no".
  function cartHasItems() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) { return !!(cart && cart.item_count > 0); })
      .catch(function () { return false; });
  }

  // Open the modal PROMPT_DELAY_MS from now. Re-calling restarts the timer, so
  // two quick Add to Cart clicks still produce a single prompt.
  function schedulePrompt() {
    if (!shouldShowModal()) return;
    if (promptTimer) clearTimeout(promptTimer);
    promptTimer = setTimeout(function () {
      promptTimer = null;
      if (!shouldShowModal() || isModalOpen()) return; // re-check after delay
      cartHasItems().then(function (hasItems) {
        if (hasItems && shouldShowModal() && !isModalOpen()) openModal();
      });
    }, PROMPT_DELAY_MS);
  }

  // ---- Add to Cart click (Sample ATC and other [data-add-to-cart] buttons) ----
  // Never blocks or delays the add itself — the click goes straight through and
  // we only start the timer. The configurator's #add-to-cart-button is not a
  // [data-add-to-cart] element: it adds via fetch then redirects to /cart, where
  // initCartPageModal below picks it up.

  function watchAddToCart() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-add-to-cart]')) return;
      schedulePrompt();
    }, true); // capture phase — runs even if a theme handler stops propagation
  }

  // ---- Cart page auto-trigger ----
  // After the configurator adds an item and redirects to /cart, prompt
  // anonymous customers to save their email once PROMPT_DELAY_MS has passed.

  function initCartPageModal() {
    if (window.location.pathname !== '/cart') return;
    schedulePrompt();
  }

  // ---- Boot ----

  function init() {
    attachModalHandlers();
    watchAddToCart();
    initCartPageModal();
  }

  // Public API so other flows can open this modal and await its resolution.
  // (The "Preview & Show Price" editor button no longer uses it — the prompt is
  // add-to-cart only now.)
  //   open()        -> Promise that resolves when the modal is closed/skipped
  //                    (note: "Sign in with Shop" redirects away rather than resolving)
  //   shouldShow()  -> true when an anonymous, not-yet-seen-this-session shopper should see it
  window.AccountSaveModal = {
    open: openModal,
    shouldShow: shouldShowModal,
    isLoggedIn: isLoggedIn
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
